import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Modal from "./Modal";
import Typography from "../Typography";
import Button from "../Button/Button";
import { theme } from "../../theme";
import MotorikStripeIcon from "../Icons/MotorikStripeIcon";

const StripeConnectionOptionsModal = ({
	isOpen,
	onClose,
	onCreateNew,
	onConnectExisting,
	onShowReactivation,
	showReactivationOption = false,
	isCreatingNew = false,
	isConnectingExisting = false,
}) => {
	const { t } = useTranslation(["userProfile", "hostedEvents"]);

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			title={t(
				"userProfile:stripe.connectionOptions.title",
				"Conectar con Stripe",
			)}
			onClose={onClose}
			maxWidth="500px"
		>
			<ModalContent>
				<Header>
					<MotorikStripeIcon width={80} height={32} />
					<Description
						$variant="body-1-medium"
						color={theme.colors.defaultWeak}
						$align="center"
					>
						{t(
							"userProfile:stripe.connectionOptions.description",
							"Elige cómo quieres conectar tu cuenta de Stripe para recibir pagos.",
						)}
					</Description>
				</Header>

				<OptionsContainer>
					<OptionCard>
						<OptionContent>
							<Typography
								$variant="body-1-semibold"
								color={theme.colors.defaultMain}
							>
								{t(
									"userProfile:stripe.connectionOptions.createNew.title",
									"Crear nueva cuenta",
								)}
							</Typography>
							<Typography
								$variant="body-2-medium"
								color={theme.colors.defaultWeak}
							>
								{t(
									"userProfile:stripe.connectionOptions.createNew.description",
									"Crea una nueva cuenta de Stripe desde cero. Ideal si es tu primera vez usando Stripe.",
								)}
							</Typography>
						</OptionContent>
						<Button
							$variant="primary"
							$size="medium"
							onClick={onCreateNew}
							loading={isCreatingNew}
							disabled={isConnectingExisting}
							$fullWidth
						>
							{t(
								"userProfile:stripe.connectionOptions.createNew.button",
								"Crear nueva",
							)}
						</Button>
					</OptionCard>

					<OptionCard>
						<OptionContent>
							<Typography
								$variant="body-1-semibold"
								color={theme.colors.defaultMain}
							>
								{t(
									"userProfile:stripe.connectionOptions.connectExisting.title",
									"Conectar cuenta existente",
								)}
							</Typography>
							<Typography
								$variant="body-2-medium"
								color={theme.colors.defaultWeak}
							>
								{t(
									"userProfile:stripe.connectionOptions.connectExisting.description",
									"Si ya tienes una cuenta de Stripe verificada, conectala para recibir pagos.",
								)}
							</Typography>
						</OptionContent>
						<Button
							$variant="outline"
							$size="medium"
							onClick={onConnectExisting}
							loading={isConnectingExisting}
							disabled={isCreatingNew}
							$fullWidth
						>
							{t(
								"userProfile:stripe.connectionOptions.connectExisting.button",
								"Conectar existente",
							)}
						</Button>
					</OptionCard>

					{showReactivationOption && (
						<OptionCard>
							<OptionContent>
								<Typography
									$variant="body-1-semibold"
									color={theme.colors.defaultMain}
								>
									{t(
										"userProfile:stripe.connectionOptions.reactivate.title",
										"Reactivar cuenta anterior",
									)}
								</Typography>
								<Typography
									$variant="body-2-medium"
									color={theme.colors.defaultWeak}
								>
									{t(
										"userProfile:stripe.connectionOptions.reactivate.description",
										"Si ya conectaste Stripe antes, puedes reactivar una cuenta previa sin empezar de cero.",
									)}
								</Typography>
							</OptionContent>
							<Button
								$variant="outline"
								$size="medium"
								onClick={onShowReactivation}
								disabled={isCreatingNew || isConnectingExisting}
								$fullWidth
							>
								{t(
									"userProfile:stripe.connectionOptions.reactivate.button",
									"Ver cuentas anteriores",
								)}
							</Button>
						</OptionCard>
					)}
				</OptionsContainer>
			</ModalContent>
		</Modal>
	);
};

const ModalContent = styled.div`
  padding: ${theme.sizing.sm};
  padding-top: ${theme.sizing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.md};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.sizing.md};
  text-align: center;
`;

const Description = styled(Typography)`
  max-width: 320px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const OptionCard = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: fit-content;
  align-items: flex-start;
  gap: ${theme.sizing.sm};
  padding: ${theme.sizing.sm};
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.sm};
  // background: ${theme.fill.defaultSubtle};
  transition: all 0.2s ease;

  &:hover {
    // border-color: ${theme.border.defaultSubtle};
    background: ${theme.fill.defaultSubtle};
  }
`;

const OptionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.xs};
`;
export default StripeConnectionOptionsModal;
